'use client';

/**
 * Chewy's Rescue Mission - Main Page Component
 *
 * The hero Chewy helps dig up your buried treasures from
 * the graveyard of rugged protocols!
 */
import { c as _c } from "react/compiler-runtime";
import { useState, useMemo } from 'react';
import { useChainId, useConnection } from 'wagmi';
import { isAddress } from 'viem';
import { Tabs, Tab, Button, Chip, Input, Switch } from '@heroui/react';
import { Skull, Shield, Search, Shovel, AlertTriangle, Users, User, X } from 'lucide-react';
import Col from '@/components/Layout/Flex/Col';
import Row from '@/components/Layout/Flex/Row';
import ConnectButton from '@/components/ConnectButton';
import { useHallOfShame } from '@/hooks/masterchef/useHallOfShame';
import { useUserStuckFunds } from '@/hooks/masterchef/useUserStuckFunds';
import RescueHero from './components/RescueHero';
import RescueStats from './components/RescueStats';
import RecoveryStats from './components/RecoveryStats';
import TipCreator from './components/TipCreator';
import ProtocolGraveCard from './components/ProtocolGraveCard';
import UserStuckFundsSection from './components/UserStuckFundsSection';
import { ChainId } from '@/constants/chain-specific';
type ViewTab = 'my-rescue' | 'graveyard';
export default function RescuePage() {
  const $ = _c(73);
  const wagmiChainId = useChainId();
  const {
    isConnected
  } = useConnection();
  const [activeTab, setActiveTab] = useState("my-rescue");
  const chainId = isConnected ? wagmiChainId : ChainId.DOGECHAIN;
  const [isCheckingFriend, setIsCheckingFriend] = useState(false);
  const [friendAddress, setFriendAddress] = useState("");
  let t0;
  if ($[0] !== friendAddress) {
    t0 = friendAddress ? isAddress(friendAddress) : false;
    $[0] = friendAddress;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const friendAddressValid = t0;
  const {
    protocols,
    isLoading: isLoadingProtocols,
    error: protocolsError,
    refetch: refetchProtocols
  } = useHallOfShame(chainId);
  const t1 = isCheckingFriend && friendAddressValid ? friendAddress : undefined;
  let t2;
  if ($[2] !== t1) {
    t2 = {
      targetAddress: t1
    };
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    positions: stuckPositions,
    isLoading: isLoadingPositions,
    isScanning,
    scan: scanForFunds,
    protocolsWithFunds,
    scannedAddress,
    isCheckingFriend: hookIsCheckingFriend,
    hasScanned
  } = useUserStuckFunds(protocols, t2);
  let t3;
  if ($[4] !== protocols) {
    t3 = protocols.reduce(_temp, 0);
    $[4] = protocols;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const totalPools = t3;
  let t4;
  if ($[6] !== protocols) {
    t4 = protocols.reduce(_temp2, 0);
    $[6] = protocols;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const nonEmptyPools = t4;
  let t5;
  if ($[8] !== nonEmptyPools || $[9] !== protocols.length || $[10] !== protocolsWithFunds || $[11] !== stuckPositions.length || $[12] !== totalPools) {
    t5 = {
      totalProtocols: protocols.length,
      totalPools,
      nonEmptyPools,
      userPositionsCount: stuckPositions.length,
      protocolsWithFunds
    };
    $[8] = nonEmptyPools;
    $[9] = protocols.length;
    $[10] = protocolsWithFunds;
    $[11] = stuckPositions.length;
    $[12] = totalPools;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const stats = t5;
  let t6;
  if ($[14] !== isConnected || $[15] !== scanForFunds) {
    t6 = () => {
      if (!isConnected) {
        return;
      }
      scanForFunds();
    };
    $[14] = isConnected;
    $[15] = scanForFunds;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const handleScan = t6;
  let t7;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <RescueHero />;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== isLoadingProtocols || $[19] !== stats.totalPools || $[20] !== stats.totalProtocols || $[21] !== stats.userPositionsCount) {
    t8 = <RescueStats totalProtocols={stats.totalProtocols} totalPools={stats.totalPools} userPositions={stats.userPositionsCount} isLoading={isLoadingProtocols} />;
    $[18] = isLoadingProtocols;
    $[19] = stats.totalPools;
    $[20] = stats.totalProtocols;
    $[21] = stats.userPositionsCount;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t10;
  let t9;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <RecoveryStats />;
    t10 = <TipCreator />;
    $[23] = t10;
    $[24] = t9;
  } else {
    t10 = $[23];
    t9 = $[24];
  }
  let t11;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = key => setActiveTab(key as ViewTab);
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      tabList: "gap-6 w-full relative rounded-none p-0 border-b border-divider",
      cursor: "w-full bg-warning",
      tab: "max-w-fit px-0 h-12",
      tabContent: "group-data-[selected=true]:text-warning"
    };
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  let t14;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Shield className="w-4 h-4" />;
    t14 = <span>My Rescue</span>;
    $[27] = t13;
    $[28] = t14;
  } else {
    t13 = $[27];
    t14 = $[28];
  }
  let t15;
  if ($[29] !== stats.userPositionsCount) {
    t15 = stats.userPositionsCount > 0 && <Chip size="sm" color="danger" variant="flat">{stats.userPositionsCount}</Chip>;
    $[29] = stats.userPositionsCount;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== t15) {
    t16 = <Tab key="my-rescue" title={<div className="flex items-center gap-2">{t13}{t14}{t15}</div>} />;
    $[31] = t15;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  let t18;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <Skull className="w-4 h-4" />;
    t18 = <span>The Graveyard</span>;
    $[33] = t17;
    $[34] = t18;
  } else {
    t17 = $[33];
    t18 = $[34];
  }
  let t19;
  if ($[35] !== stats.totalProtocols) {
    t19 = <Tab key="graveyard" title={<div className="flex items-center gap-2">{t17}{t18}<Chip size="sm" color="default" variant="flat">{stats.totalProtocols}</Chip></div>} />;
    $[35] = stats.totalProtocols;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] !== activeTab || $[38] !== t16 || $[39] !== t19) {
    t20 = <Tabs selectedKey={activeTab} onSelectionChange={t11} variant="underlined" color="warning" classNames={t12}>{t16}{t19}</Tabs>;
    $[37] = activeTab;
    $[38] = t16;
    $[39] = t19;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] !== activeTab || $[42] !== friendAddress || $[43] !== friendAddressValid || $[44] !== handleScan || $[45] !== hasScanned || $[46] !== hookIsCheckingFriend || $[47] !== isCheckingFriend || $[48] !== isConnected || $[49] !== isLoadingPositions || $[50] !== isScanning || $[51] !== scannedAddress || $[52] !== stats.totalProtocols || $[53] !== stuckPositions) {
    t21 = activeTab === "my-rescue" && <Col className="gap-6">{isConnected && <div className="p-4 bg-content1 rounded-xl"><Row className="items-center justify-between gap-4 flex-wrap"><Row className="items-center gap-3"><div className="p-2 rounded-lg bg-warning/20">{isCheckingFriend ? <Users className="w-5 h-5 text-warning" /> : <User className="w-5 h-5 text-warning" />}</div><div><p className="font-semibold">{isCheckingFriend ? "Checking Friend's Wallet" : "Checking Your Wallet"}</p><p className="text-sm text-foreground-500">{isCheckingFriend ? "Enter a fren's wallet address" : "Toggle to check a friend's wallet instead"}</p></div></Row><Switch isSelected={isCheckingFriend} onValueChange={setIsCheckingFriend} color="primary" size="lg"><span className="text-sm">Check for Friend</span></Switch></Row>{isCheckingFriend && <div className="mt-4"><Input value={friendAddress} onValueChange={setFriendAddress} placeholder="Enter wallet address (0x...)" size="lg" variant="bordered" color={friendAddress && !friendAddressValid ? "danger" : "default"} description={friendAddress && !friendAddressValid ? "Please enter a valid Ethereum address" : friendAddressValid ? `Will scan ${friendAddress.slice(0, 6)}...${friendAddress.slice(-4)}` : "Enter the wallet address you want to check"} endContent={friendAddress && <button onClick={() => setFriendAddress("")} className="p-1 hover:bg-default-200 rounded-full transition-colors"><X className="w-4 h-4 text-foreground-500" /></button>} classNames={{
            input: ""
          }} /></div>}{hookIsCheckingFriend && scannedAddress && <div className="mt-3 p-3 bg-primary/10 border border-primary/30 rounded-lg"><Row className="items-center gap-2"><Users className="w-4 h-4 text-primary" /><span className="text-sm">Showing results for:{" "}<span className="font-mono font-semibold text-primary">{scannedAddress.slice(0, 6)}...{scannedAddress.slice(-4)}</span></span></Row></div>}</div>}{!isConnected ? <Col className="items-center justify-center py-16 gap-6 bg-content1 rounded-2xl"><div className="w-32 h-32 rounded-full bg-warning/20 flex items-center justify-center"><Shovel className="w-16 h-16 text-warning" /></div><h3 className="text-2xl font-bold text-center">Connect Wallet to Start Rescue</h3><p className="text-foreground-500 text-center max-w-md">Connect your wallet and let Chewy sniff out any funds you might have stuck in abandoned protocols.</p><ConnectButton /></Col> : !hasScanned && !isScanning ? <Col className="items-center justify-center py-16 gap-6 bg-content1 rounded-2xl"><div className="w-32 h-32 rounded-full bg-success/20 flex items-center justify-center animate-bounce"><Search className="w-16 h-16 text-success" /></div><h3 className="text-2xl font-bold text-center">{isCheckingFriend ? "Ready to Help a Friend" : "Ready to Search"}</h3><p className="text-foreground-500 text-center max-w-xs md:max-w-lg">{isCheckingFriend ? friendAddressValid ? <>Click the button below and Chewy will dig through{" "}<span className="text-warning font-semibold">{stats.totalProtocols} abandoned protocols</span>{" "}to find any buried treasure for{" "}<span className="text-sm text-primary font-semibold">{friendAddress.slice(0, 6)}...{friendAddress.slice(-4)}</span>!</> : <span>Enter a valid wallet address above to search for your friend's stuck funds.</span> : <>Click the button below and Chewy will dig through{" "}<span className="text-warning font-semibold">{stats.totalProtocols} abandoned protocols</span>{" "}to find any of your buried treasure!</>}</p><Button color="warning" size="lg" className="font-bold text-black" onPress={handleScan} isLoading={isScanning || isLoadingPositions} isDisabled={isCheckingFriend && !friendAddressValid}><Shovel className="w-5 h-5" />{isCheckingFriend ? "Dig for Friend!" : "Start Digging!"}</Button></Col> : <UserStuckFundsSection positions={stuckPositions} isScanning={isScanning} onRescan={handleScan} scannedAddress={scannedAddress ?? undefined} isCheckingFriend={hookIsCheckingFriend} />}</Col>;
    $[41] = activeTab;
    $[42] = friendAddress;
    $[43] = friendAddressValid;
    $[44] = handleScan;
    $[45] = hasScanned;
    $[46] = hookIsCheckingFriend;
    $[47] = isCheckingFriend;
    $[48] = isConnected;
    $[49] = isLoadingPositions;
    $[50] = isScanning;
    $[51] = scannedAddress;
    $[52] = stats.totalProtocols;
    $[53] = stuckPositions;
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  let t22;
  if ($[55] !== activeTab || $[56] !== handleScan || $[57] !== isLoadingProtocols || $[58] !== isScanning || $[59] !== protocols || $[60] !== protocolsError || $[61] !== refetchProtocols || $[62] !== stuckPositions) {
    t22 = activeTab === "graveyard" && <Col className="gap-6"><div className="flex items-center gap-3 p-4 bg-danger/10 border border-danger/30 rounded-xl"><AlertTriangle className="w-6 h-6 text-danger flex-shrink-0" /><div><h4 className="font-semibold text-danger">The Protocol Graveyard</h4><p className="text-sm text-foreground-500">These protocols have been abandoned, deprecated, or rugged. Check if you have any funds stuck and recover them safely!</p></div></div>{isLoadingProtocols ? <Row className="justify-center py-16"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-warning" /></Row> : protocolsError ? <Col className="items-center py-16 gap-4"><p className="text-danger">Failed to load protocols: {protocolsError.message}</p><Button onPress={refetchProtocols}>Retry</Button></Col> : protocols.length === 0 ? <Col className="items-center py-16 gap-4 bg-content1 rounded-2xl"><div className="text-6xl"><span role="img" aria-label="celebration">🎉</span></div><h3 className="text-xl font-bold">No Dead Protocols Found!</h3><p className="text-foreground-500">Good news! There are no abandoned protocols tracked on this chain.</p></Col> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 justify-items-center">{protocols.map((protocol, index) => <ProtocolGraveCard key={protocol.protocol.slug} protocol={protocol} index={index} userHasFunds={stuckPositions.some(p_1 => p_1.protocol.slug === protocol.protocol.slug)} onCheckFunds={() => {
          setActiveTab("my-rescue");
          if (!isScanning) {
            handleScan();
          }
        }} />)}</div>}</Col>;
    $[55] = activeTab;
    $[56] = handleScan;
    $[57] = isLoadingProtocols;
    $[58] = isScanning;
    $[59] = protocols;
    $[60] = protocolsError;
    $[61] = refetchProtocols;
    $[62] = stuckPositions;
    $[63] = t22;
  } else {
    t22 = $[63];
  }
  let t23;
  if ($[64] !== t21 || $[65] !== t22) {
    t23 = <div className="mt-6">{t21}{t22}</div>;
    $[64] = t21;
    $[65] = t22;
    $[66] = t23;
  } else {
    t23 = $[66];
  }
  let t24;
  if ($[67] !== t20 || $[68] !== t23) {
    t24 = <div className="w-full">{t20}{t23}</div>;
    $[67] = t20;
    $[68] = t23;
    $[69] = t24;
  } else {
    t24 = $[69];
  }
  let t25;
  if ($[70] !== t24 || $[71] !== t8) {
    t25 = <Col className="w-full max-w-6xl mx-auto gap-8">{t7}{t8}{t9}{t10}{t24}</Col>;
    $[70] = t24;
    $[71] = t8;
    $[72] = t25;
  } else {
    t25 = $[72];
  }
  return t25;
}
function _temp2(acc_0, p_0) {
  return acc_0 + p_0.totalNonEmptyPools;
}
function _temp(acc, p) {
  return acc + p.totalPools;
}